<template>
  <v-container fluid class="pa-8">
    <!-- Header -->
    <v-row class="mb-8">
      <v-col cols="12">
        <h1 class="brand-text-gradient text-h2 font-weight-bold mb-2">
          Theme System Demo
        </h1>
        <p class="text-secondary-muted text-h6">
          Explore the complete brand color palette and theme components
        </p>
      </v-col>
    </v-row>

    <!-- Color Palette -->
    <v-row class="mb-8">
      <v-col cols="12">
        <v-card class="premium-card">
          <v-card-title class="text-h5 font-weight-bold">
            🎨 Brand Color Palette
          </v-card-title>
          <v-card-text>
            <v-row>
              <!-- Primary Solid -->
              <v-col cols="12" md="4">
                <div
                  class="color-box"
                  style="background: #FF771C"
                >
                  <div class="color-label">Primary Solid</div>
                  <div class="color-hex">#FF771C</div>
                </div>
              </v-col>

              <!-- Secondary 1 -->
              <v-col cols="12" md="4">
                <div
                  class="color-box"
                  style="background: #F6AB2E"
                >
                  <div class="color-label">Secondary 1</div>
                  <div class="color-hex">#F6AB2E</div>
                </div>
              </v-col>

              <!-- Secondary 2 -->
              <v-col cols="12" md="4">
                <div
                  class="color-box"
                  style="background: #E7531A"
                >
                  <div class="color-label">Secondary 2</div>
                  <div class="color-hex">#E7531A</div>
                </div>
              </v-col>

              <!-- Primary Gradient -->
              <v-col cols="12" md="6">
                <div
                  class="color-box"
                  style="background: linear-gradient(135deg, #FF771C, #F6AB2E)"
                >
                  <div class="color-label">Primary Gradient</div>
                  <div class="color-hex">#FF771C → #F6AB2E</div>
                </div>
              </v-col>

              <!-- Grayscale -->
              <v-col cols="12" md="6">
                <div class="color-box bg-surface border-subtle">
                  <div class="color-label text-primary-dark">Surface</div>
                  <div class="color-hex text-secondary-muted">Auto-themed</div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Buttons -->
    <v-row class="mb-8">
      <v-col cols="12">
        <v-card class="premium-card">
          <v-card-title class="text-h5 font-weight-bold">
            🔘 Buttons
          </v-card-title>
          <v-card-text>
            <div class="d-flex flex-wrap ga-3">
              <v-btn color="primary" large>
                Primary Button
              </v-btn>
              <v-btn color="secondary" large>
                Secondary Button
              </v-btn>
              <v-btn color="success" large>
                Success Button
              </v-btn>
              <v-btn color="error" large>
                Error Button
              </v-btn>
              <v-btn outlined color="primary" large>
                Outlined Button
              </v-btn>
              <v-btn text color="primary" large>
                Text Button
              </v-btn>
              <v-btn
                color="primary"
                large
                elevation="0"
                class="brand-gradient"
              >
                Gradient Button
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Cards & Shadows -->
    <v-row class="mb-8">
      <v-col cols="12" md="4">
        <v-card class="premium-card">
          <v-card-title>
            <v-icon color="primary" left>mdi-chart-line</v-icon>
            Premium Card
          </v-card-title>
          <v-card-text>
            <div class="text-h3 text-primary-dark mb-2">1,284</div>
            <div class="text-secondary-muted">Total Users</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card class="premium-card glow-effect">
          <v-card-title>
            <v-icon color="secondary" left>mdi-fire</v-icon>
            Glow Card (Dark)
          </v-card-title>
          <v-card-text>
            <div class="text-h3 text-primary-dark mb-2">92%</div>
            <div class="text-secondary-muted">Performance</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <div class="brand-gradient rounded-premium pa-6">
          <div class="text-h6 white--text font-weight-bold mb-2">
            Gradient Card
          </div>
          <div class="white--text body-2">
            With brand gradient background
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Badges & Chips -->
    <v-row class="mb-8">
      <v-col cols="12">
        <v-card class="premium-card">
          <v-card-title class="text-h5 font-weight-bold">
            🏷️ Badges & Chips
          </v-card-title>
          <v-card-text>
            <div class="d-flex flex-wrap ga-2 mb-4">
              <v-chip color="primary" label>
                Primary Chip
              </v-chip>
              <v-chip color="secondary" label>
                Secondary Chip
              </v-chip>
              <v-chip color="success" label>
                <v-icon left small>mdi-check-circle</v-icon>
                Success
              </v-chip>
              <v-chip color="warning" label>
                <v-icon left small>mdi-alert</v-icon>
                Warning
              </v-chip>
              <v-chip color="error" label>
                <v-icon left small>mdi-close-circle</v-icon>
                Error
              </v-chip>
              <v-chip outlined color="primary" label>
                Outlined
              </v-chip>
            </div>

            <div class="d-flex flex-wrap ga-2">
              <v-badge color="error" content="5" overlap>
                <v-icon>mdi-bell</v-icon>
              </v-badge>
              <v-badge color="success" content="New" overlap>
                <v-icon>mdi-email</v-icon>
              </v-badge>
              <v-badge color="primary" dot overlap>
                <v-icon>mdi-account</v-icon>
              </v-badge>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Form Elements -->
    <v-row class="mb-8">
      <v-col cols="12" md="6">
        <v-card class="premium-card">
          <v-card-title class="text-h5 font-weight-bold">
            📝 Form Elements
          </v-card-title>
          <v-card-text>
            <v-text-field
              label="Text Field"
              outlined
              placeholder="Enter text"
              color="primary"
              class="mb-4"
            />

            <v-select
              :items="['Option 1', 'Option 2', 'Option 3']"
              label="Select Field"
              outlined
              color="primary"
              class="mb-4"
            />

            <v-textarea
              label="Textarea"
              outlined
              rows="3"
              color="primary"
              class="mb-4"
            />

            <v-switch
              label="Toggle Switch"
              color="primary"
              class="mt-0"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="premium-card">
          <v-card-title class="text-h5 font-weight-bold">
            ⚡ Progress & Loading
          </v-card-title>
          <v-card-text>
            <div class="mb-4">
              <div class="text-caption mb-2">Primary Progress</div>
              <v-progress-linear
                value="75"
                color="primary"
                height="8"
                rounded
              />
            </div>

            <div class="mb-4">
              <div class="text-caption mb-2">Secondary Progress</div>
              <v-progress-linear
                value="60"
                color="secondary"
                height="8"
                rounded
              />
            </div>

            <div class="mb-4">
              <div class="text-caption mb-2">Gradient Progress</div>
              <v-progress-linear
                value="85"
                height="8"
                rounded
                class="brand-gradient"
              />
            </div>

            <div class="d-flex ga-4 mt-6">
              <v-progress-circular
                indeterminate
                color="primary"
              />
              <v-progress-circular
                :value="75"
                color="secondary"
                size="50"
                width="5"
              >
                75%
              </v-progress-circular>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Typography -->
    <v-row class="mb-8">
      <v-col cols="12">
        <v-card class="premium-card">
          <v-card-title class="text-h5 font-weight-bold">
            📖 Typography
          </v-card-title>
          <v-card-text>
            <h1 class="text-h1 text-primary-dark mb-2">Heading 1</h1>
            <h2 class="text-h2 text-primary-dark mb-2">Heading 2</h2>
            <h3 class="text-h3 text-primary-dark mb-2">Heading 3</h3>
            <h4 class="text-h4 brand-text-gradient mb-2">Heading 4 (Gradient)</h4>
            <p class="text-body-1 text-primary-dark mb-2">
              Body text with primary color automatically adapts to theme
            </p>
            <p class="text-body-2 text-secondary-muted mb-2">
              Secondary text for less important information
            </p>
            <p class="text-caption text-secondary-muted">
              Caption text for metadata and timestamps
            </p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Alerts -->
    <v-row class="mb-8">
      <v-col cols="12">
        <v-card class="premium-card">
          <v-card-title class="text-h5 font-weight-bold">
            🔔 Alerts
          </v-card-title>
          <v-card-text>
            <v-alert
              type="success"
              text
              class="mb-3"
              border="left"
            >
              Success alert with auto-themed colors
            </v-alert>

            <v-alert
              type="info"
              text
              class="mb-3"
              border="left"
            >
              Info alert for general information
            </v-alert>

            <v-alert
              type="warning"
              text
              class="mb-3"
              border="left"
            >
              Warning alert to grab attention
            </v-alert>

            <v-alert
              type="error"
              text
              border="left"
            >
              Error alert for critical messages
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Data Table -->
    <v-row>
      <v-col cols="12">
        <v-card class="premium-card">
          <v-card-title class="text-h5 font-weight-bold">
            📊 Data Table
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="tableHeaders"
              :items="tableItems"
              class="elevation-0"
            >
              <template v-slot:item.status="{ item }">
                <v-chip
                  small
                  :color="item.status === 'Active' ? 'success' : 'secondary'"
                  label
                >
                  {{ item.status }}
                </v-chip>
              </template>
              <template v-slot:item.progress="{ item }">
                <v-progress-linear
                  :value="item.progress"
                  color="primary"
                  height="6"
                  rounded
                />
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: 'ThemeDemo',

  data() {
    return {
      tableHeaders: [
        { text: 'Name', value: 'name' },
        { text: 'Status', value: 'status' },
        { text: 'Progress', value: 'progress' },
        { text: 'Value', value: 'value' },
      ],
      tableItems: [
        { name: 'Project Alpha', status: 'Active', progress: 85, value: '$12,500' },
        { name: 'Project Beta', status: 'Active', progress: 60, value: '$8,900' },
        { name: 'Project Gamma', status: 'Inactive', progress: 25, value: '$4,200' },
        { name: 'Project Delta', status: 'Active', progress: 95, value: '$15,600' },
      ]
    }
  }
}
</script>

<style scoped>
.color-box {
  padding: 48px 24px;
  border-radius: 12px;
  text-align: center;
  transition: transform 0.3s ease;
}

.color-box:hover {
  transform: translateY(-4px);
}

.color-label {
  color: white;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 8px;
}

.color-hex {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-family: 'Monaco', 'Courier New', monospace;
}
</style>
